import React, { type HTMLAttributes } from 'react';
import { tv } from 'tailwind-variants';
import type { VariantProps } from 'tailwind-variants';
import { FontWeight, Heading, useFontClass } from '../../tokens';
import { LiliRadioInput, type LiliRadioInputProps } from '../LiliRadioInput';

const liliRadioBoxClassNames = tv({
  slots: {
    wrapper:
      'transition ease-in flex flex-col justify-center rounded-[12px] border cursor-pointer group',
    mainRow: 'flex items-center justify-between',
    left: 'flex flex-1 items-center min-w-0',
    icon: 'shrink-0 w-[24px] h-[24px] flex items-center justify-center text-text-main-primary',
    textColumn: 'flex flex-col flex-1 min-w-0',
    titleRow: 'flex items-center',
    title: '',
    text: 'text-text-main-secondary',
    content:
      'grid grid-rows-[0fr] transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
    contentInner: 'min-h-0 overflow-hidden',
    /** Hover class for the unchecked radio indicator (passed to `LiliRadioInput`). */
    radio: '',
  },
  variants: {
    variant: {
      /** The general-purpose selectable row (entity/role selection). */
      default: {
        wrapper:
          'py-[14px] pl-[14px] pr-[18px] min-h-[56px] border-outline-primary-bw-04 hover:border-outline-brand-primary',
        left: 'gap-[10px] mr-[12px]',
        titleRow: 'gap-[2px]',
        radio: 'group-hover:text-outline-brand-primary',
      },
      /**
       * A compact card whose content is full-bleed: the header row owns its padding and the
       * revealed `content` provides its own (used by plan/pricing options).
       */
      card: {
        wrapper:
          'bg-surface-primary-bw-00 border-outline-primary-bw-03 hover:border-outline-primary-bw-05',
        mainRow: 'gap-[8px] p-[12px]',
        left: 'gap-[8px]',
        titleRow: 'gap-[8px]',
        radio: 'group-hover:text-outline-primary-bw-05',
      },
    },
    checked: {
      true: {
        wrapper: 'border-outline-brand-primary hover:border-outline-brand-primary',
        content: 'grid-rows-[1fr]',
      },
    },
    disabled: {
      true: {
        wrapper: 'border-none cursor-not-allowed opacity-50',
        title: 'text-text-main-tertiary',
        text: 'text-text-main-tertiary',
      },
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

type LiliRadioBoxVariants = VariantProps<typeof liliRadioBoxClassNames>;

export interface LiliRadioBoxProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onClick' | 'content'
> {
  /** Optional icon to display on the left side */
  icon?: React.ReactNode;
  title: string;
  /** Optional slot to render next to the title (e.g., tooltip icon, badge) */
  titleSlot?: React.ReactNode;
  text?: string;
  disabled?: boolean;
  value: string | number;
  checked?: boolean;
  name: string;
  onClick?: (value: string | number) => void;
  id?: string;
  radioButtonOverrideProps?: Partial<LiliRadioInputProps>;
  /** Visual treatment. `default` is the general row; `card` is the compact full-bleed card. */
  variant?: LiliRadioBoxVariants['variant'];
  /**
   * Content revealed below the option while it is selected (e.g. billing options, a
   * follow-up field or extra detail). Content-agnostic — the box only owns the reveal.
   */
  content?: React.ReactNode;
  /** Class name applied to the revealed content wrapper. */
  contentClassName?: string;
  /**
   * Animate the reveal of `content`. When enabled the content stays mounted while collapsed
   * but is marked `inert` and `aria-hidden`, so it can neither be focused nor announced.
   * Defaults to `false`, so the content is mounted only while selected.
   */
  animateContent?: boolean;
}

export const LiliRadioBox = (props: LiliRadioBoxProps) => {
  const {
    icon,
    title,
    titleSlot,
    text,
    disabled = false,
    value,
    checked = false,
    name,
    onClick,
    className,
    id,
    radioButtonOverrideProps,
    variant = 'default',
    content,
    contentClassName,
    animateContent = false,
    ...divProps
  } = props;

  const { fontClass: fontClassMedium } = useFontClass(FontWeight.MEDIUM);
  const { fontClass } = useFontClass();

  const {
    wrapper,
    mainRow,
    left,
    icon: iconClass,
    textColumn,
    titleRow,
    title: titleClass,
    text: textClass,
    content: contentClass,
    contentInner,
    radio: radioClass,
  } = liliRadioBoxClassNames({ variant, checked, disabled });

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick(value);
    }
  };

  // The card treatment uses a smaller title to match the pricing/plan option design.
  const TitleHeading = variant === 'card' ? Heading.H10 : Heading.H9;

  return (
    <div id={id} {...divProps} onClick={handleClick} className={wrapper({ class: className })}>
      <div className={mainRow()}>
        <div className={left()}>
          {icon && <div className={iconClass()}>{icon}</div>}

          <div className={textColumn()}>
            <div className={titleRow()}>
              <TitleHeading className={`${fontClassMedium} ${titleClass()}`}>{title}</TitleHeading>
              {titleSlot && <div className='shrink-0'>{titleSlot}</div>}
            </div>

            {text && <Heading.H10 className={`${fontClass} ${textClass()}`}>{text}</Heading.H10>}
          </div>
        </div>

        <LiliRadioInput
          id={`${id}-input`}
          name={name}
          checked={checked}
          disabled={disabled}
          onChange={() => handleClick()}
          uncheckedHoverClassName={radioClass()}
          stopClickPropagation
          {...radioButtonOverrideProps}
        />
      </div>

      {content != null &&
        (animateContent ? (
          <div
            aria-hidden={!checked}
            inert={!checked}
            data-testid={id ? `${id}-content` : undefined}
            className={contentClass({ class: contentClassName })}>
            <div className={contentInner()}>{content}</div>
          </div>
        ) : (
          checked && <div className={contentClassName}>{content}</div>
        ))}
    </div>
  );
};
